import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PageBlock, BlockType } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import AdminImageUpload from "@/components/feature/AdminImageUpload";

// ─── Block type definitions ───────────────────────────────────────────────

export const BLOCK_TYPES: { type: BlockType; icon: string; label: string; desc: string }[] = [
  { type: "heading", icon: "ri-heading", label: "Heading", desc: "Section title (H1-H4)" },
  { type: "text", icon: "ri-text", label: "Text Block", desc: "Paragraph with optional card/gradient style" },
  { type: "image", icon: "ri-image-line", label: "Single Image", desc: "Image with size & caption" },
  { type: "image-grid", icon: "ri-gallery-line", label: "Image Grid", desc: "Multiple images in a row" },
  { type: "image-detail", icon: "ri-image-2-line", label: "Image + Details", desc: "Image with title & description" },
  { type: "two-col", icon: "ri-layout-column-line", label: "Two Columns", desc: "Text or image on either side" },
  { type: "cta", icon: "ri-cursor-line", label: "Call to Action", desc: "Headline + button" },
  { type: "divider", icon: "ri-separator", label: "Divider", desc: "Horizontal rule / spacer" },
  { type: "section", icon: "ri-layout-masonry-line", label: "Imported Section", desc: "Full home-page section with original layout" },
];

export function newBlock(type: BlockType): PageBlock {
  const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  switch (type) {
    case "heading": return { id, type, content: "New Section Heading", headingLevel: "h2" };
    case "text": return { id, type, content: "Write your content here. Click to edit this text block.", textStyle: "default" };
    case "image": return { id, type, content: "", imageAlt: "", imageSize: "full", showCaption: true };
    case "image-grid": return {
      id, type, content: "",
      images: [
        { id: `img-${Date.now()}-a`, url: "", alt: "" },
        { id: `img-${Date.now()}-b`, url: "", alt: "" },
      ],
      imageCols: 2,
    };
    case "image-detail": return { id, type, content: "", imageAlt: "Image", subContent: "Title for this image", ctaLabel: "Description text goes here." };
    case "two-col": return {
      id, type, content: "Left column text goes here.", subContent: "Right column content goes here.",
      leftType: "text", rightType: "text",
    };
    case "cta": return { id, type, content: "Ready to get started?", subContent: "Join us today and be part of something special.", ctaLabel: "Get Started", ctaUrl: "/" };
    case "section": return { id, type, content: "Imported Section", sectionMeta: { layout: "cta-banner", data: {} } };
    case "divider": return { id, type, content: "" };
  }
}

// ─── Insert Bar ───────────────────────────────────────────────────────────

export function InsertBar({ onInsert, isDark }: { onInsert: (type: BlockType) => void; isDark: boolean }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex items-center gap-2 py-2">
      <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-all ${
          isDark ? "bg-gray-700 text-gray-300 hover:bg-pink-500 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-pink-500 hover:text-white"
        }`}
      >
        <i className="ri-add-line text-xs"></i>Insert here
      </button>
      <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
      {open && (
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 z-20 mt-1 rounded-xl border p-2 grid grid-cols-4 gap-1.5 w-80 ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {BLOCK_TYPES.filter((bt) => bt.type !== "section").map((bt) => (
            <button
              key={bt.type}
              onClick={() => { onInsert(bt.type); setOpen(false); }}
              className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg cursor-pointer transition-all text-center ${isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-pink-50 text-gray-600"}`}
            >
              <i className={`${bt.icon} text-pink-400 text-base`}></i>
              <span className={`text-[10px] font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>{bt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Admin Bar for section blocks ───────────────────────────────────────────

export function SectionAdminBar({
  layout,
  isDark,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  layout: string;
  isDark: boolean;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  const btnBase = "flex items-center gap-1 px-2.5 py-1 rounded-md cursor-pointer text-xs font-bold whitespace-nowrap transition-colors";
  const btnMove = isDark
    ? "bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-gray-200"
    : "bg-white text-gray-500 hover:bg-gray-100 border border-gray-200";
  return (
    <div className={`flex items-center justify-between mb-2 rounded-lg px-2 py-1.5 ${isDark ? "bg-gray-800/80 border border-gray-700" : "bg-green-50 border border-green-200"}`}>
      <div className="flex items-center gap-1.5">
        <i className="ri-layout-masonry-line text-green-500 text-xs"></i>
        <span className="text-[10px] font-bold uppercase tracking-wider text-green-600">Imported: {layout}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={onMoveUp} disabled={isFirst} className={`${btnBase} ${btnMove} ${isFirst ? "opacity-30 cursor-not-allowed" : ""}`}>
          <i className="ri-arrow-up-line text-xs"></i> Up
        </button>
        <button onClick={onMoveDown} disabled={isLast} className={`${btnBase} ${btnMove} ${isLast ? "opacity-30 cursor-not-allowed" : ""}`}>
          <i className="ri-arrow-down-line text-xs"></i> Down
        </button>
        <button onClick={onDelete} className={`${btnBase} bg-red-500 text-white hover:bg-red-600`}>
          <i className="ri-delete-bin-line text-xs"></i> Delete
        </button>
      </div>
    </div>
  );
}

// ─── Reusable editable wrappers ─────────────────────────────────────────────

export function EditableText({
  field,
  fallback,
  children,
  className = "",
  multiline = false,
  isAdmin,
  onUpdate,
  setConfig,
}: {
  field: string;
  fallback: string;
  children: React.ReactNode;
  className?: string;
  multiline?: boolean;
  isAdmin: boolean;
  onUpdate?: (b: PageBlock) => void;
  setConfig: (key: string, value: unknown) => void;
}) {
  if (!isAdmin || !onUpdate) return <>{children}</>;
  return (
    <AdminEditField
      value={fallback}
      onSave={(v) => setConfig(field, v)}
      className={className}
      multiline={multiline}
    >
      {children}
    </AdminEditField>
  );
}

export function EditableImage({
  field,
  fallback,
  alt,
  className = "",
  imgClassName = "w-full h-full object-cover",
  objectPosition,
  onPositionChange,
  isAdmin,
  onUpdate,
  setConfig,
  isDark,
}: {
  field: string;
  fallback: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  objectPosition?: string;
  onPositionChange?: (pos: string) => void;
  isAdmin: boolean;
  onUpdate?: (b: PageBlock) => void;
  setConfig: (key: string, value: unknown) => void;
  isDark: boolean;
}) {
  const url = fallback;
  if (!isAdmin || !onUpdate) {
    return url ? <img src={url} alt={alt} className={imgClassName} style={{ objectPosition: objectPosition || "50% 50%" }} /> : null;
  }
  return (
    <AdminImageUpload
      src={url || ""}
      alt={alt}
      onReplace={(newUrl) => setConfig(field, newUrl)}
      className={className}
      imgClassName={imgClassName}
      isDark={isDark}
      objectPosition={objectPosition || "50% 50%"}
      onPositionChange={onPositionChange}
    />
  );
}

// ─── Config helpers ───────────────────────────────────────────────────────

export function useBlockConfig(block: PageBlock, onUpdate?: (b: PageBlock) => void) {
  const c = (block.config ?? {}) as Record<string, unknown>;
  const d = (block.sectionMeta?.data ?? {}) as Record<string, unknown>;
  const get = <T,>(key: string, fallback: T): T => (c[key] as T) ?? (d[key] as T) ?? fallback;

  const setConfig = (key: string, value: unknown) => {
    onUpdate?.({ ...block, config: { ...c, [key]: value } });
  };

  return { get, setConfig, c, d };
}

// ─── Non-interactive CTA button (admin display mode) ────────────────────────

export function AdminCTAButton({
  children,
  variant = "primary",
  className = "",
}: {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}) {
  const base = "inline-flex items-center justify-center font-bold px-8 py-3 rounded-full whitespace-nowrap";
  const styles = {
    primary: "bg-green-500 text-white",
    secondary: "bg-white/20 text-white border border-white/40",
    outline: "border-2 border-pink-400 text-pink-600",
  };
  return (
    <div className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </div>
  );
}

// ─── Array item delete button ───────────────────────────────────────────────

export function ArrayDeleteButton({
  onDelete,
  className = "",
}: {
  onDelete: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onDelete}
      className={`absolute -top-2 -right-2 z-10 w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full cursor-pointer shadow-sm transition-colors opacity-0 group-hover:opacity-100 ${className}`}
      title="Delete item"
    >
      <i className="ri-close-line text-xs"></i>
    </button>
  );
}

// ─── Add item button ────────────────────────────────────────────────────────

export function AddItemButton({
  onClick,
  label,
  isDark,
  className = "",
}: {
  onClick: () => void;
  label: string;
  isDark: boolean;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl border-2 border-dashed flex items-center justify-center gap-2 cursor-pointer transition-colors ${
        isDark
          ? "border-gray-600 text-gray-400 hover:border-pink-400 hover:text-pink-400"
          : "border-gray-300 text-gray-400 hover:border-pink-400 hover:text-pink-500"
      } ${className}`}
    >
      <i className="ri-add-line"></i> {label}
    </button>
  );
}